// Centralized route + BFF endpoint + scope constants. Keep paths here, not inline,
// so renames happen in one place (mirrors how LOGIN_OPTIONS centralizes auth config).

export const ROUTES = {
  home: '/',
  welcome: '/welcome',
  app: '/app',
  apply: '/apply', // EDA KYC application form (schema fetched via the BFF)
  nda: '/nda', // NDA signing screen (embeds the Omnidek-hosted NDA form)
  form: (formId: string) => `/app/forms/${formId}`,
} as const

/**
 * Omnidek-hosted destinations we send users to at TOP LEVEL (new tab), as opposed
 * to the public forms we embed via EmbeddedDocFrame.
 *
 * `portal` is behind Omnidek's own login. Link to the page itself and let Omnidek
 * start its own Auth0 -> EDF Keycloak flow: an unauthenticated request bounces to
 * /login?returnUrl=%2Fportal and comes back here afterwards. Never build or replay
 * a Keycloak authorization URL to get here — those carry a `state` Auth0 mints per
 * attempt, so a copied URL reaches the login page and then dies at the callback
 * with "we couldn't find your session".
 *
 * Also do not embed this page. Keycloak refuses to be framed (frame-ancestors
 * 'self'), and an Omnidek session established top-level does not survive into a
 * cross-origin iframe — measured, with the session confirmed live either side of
 * the test. The new tab is the mechanism, not a stylistic preference.
 */
export const OMNIDEK_URLS = {
  portal: 'https://innovateguyana.omnidek.com/portal',
} as const

export const BFF_PATHS = {
  assetData: '/me/asset-data',
  omniStatusMap: '/me/omni/statusmap', // static code → display-name table for Asset.status
  onboarding: '/me/onboarding',
  loan: '/me/loan',
  docloanUpload: '/docloan/upload', // form.io loanpro storage provider → multipart upload, brokered by the BFF
  kycForm: '/me/kyc-form', // form.io schema + caller's KYC state in one call; conditionals pre-rewired server-side
  budget: '/me/budget', // submit customer proposed grant budget datagrid
} as const

// OIDC scopes the BFF requires; requested at every Keycloak login.
// NOTE: loanpro:write is required for form.io file uploads. The edfkey-webapp
// Keycloak client MUST be configured to grant it, or login may fail with
// invalid_scope — keep this in lockstep with the server-side client config.
export const SCOPES = 'openid loanpro:read loanpro:write omnideck:read'

// Self-hosted (OSS) Form.io — the EDF KYC/application form.
// Prefer the stable id over the path: the path changed once already when the form
// was renamed ("Accelerator Upload" → "KYC Upload Form"), which 400s with
// `Invalid alias`. Render by id (`{VITE_FORMIO_URL}/form/{id}`) to be rename-proof.
export const FORMIO = {
  applicationFormId: '6a359c4784d403f0ad9cbd80',
  applicationFormPath: 'economicdevelopmentfundkycuploadform',
} as const
